import { useEffect } from 'react';
import get from 'platform/utilities/data/get';
import { maxItemsFn } from './helpers';

/**
 * Keeps the array builder yes/no field (`hasItemsKey`) in a valid state as form
 * data changes. Behavior-preserving extraction from ArrayBuilderSummaryPage.
 *
 * - On the summary page: forces the field to false once max items is reached,
 *   since the field is then hidden but still required.
 * - On the review page: forces the field to false when it is undefined, to
 *   avoid a hidden validation error.
 *
 * @param {Object} options
 * @param {ArrayBuilderOptions} options.arrayBuilderProps array builder options bag
 *   (arrayPath, maxItems, hasItemsKey, isReviewPage)
 * @param {CustomPageProps} options.customPageProps CustomPage props (data)
 * @param {React.MutableRefObject} options.dataRef ref mirroring the latest form data
 * @param {(data: Object) => void} options.setData ref-aware setData (setDataFromRef)
 */
export function useEnforceYesNo({
  arrayBuilderProps,
  customPageProps,
  dataRef,
  setData,
}) {
  const { arrayPath, hasItemsKey, isReviewPage } = arrayBuilderProps;
  const arrayData = get(arrayPath, customPageProps.data);
  const maxItems = maxItemsFn(arrayBuilderProps.maxItems, customPageProps.data);
  const arrayLength = Array.isArray(arrayData) ? arrayData.length : 0;

  useEffect(
    () => {
      const reachedMax =
        Number.isFinite(maxItems) && maxItems > 0 && arrayLength >= maxItems;

      const id = requestAnimationFrame(() => {
        const val = dataRef.current?.[hasItemsKey];

        const setFalseForReview = isReviewPage && typeof val === 'undefined';
        const setFalseForMax = !isReviewPage && reachedMax && val !== false;

        if (setFalseForMax || setFalseForReview) {
          setData({ [hasItemsKey]: false });
        }
      });

      return () => cancelAnimationFrame(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isReviewPage,
      hasItemsKey,
      maxItems,
      customPageProps.data?.[hasItemsKey],
      arrayLength,
    ],
  );
}
